import { BaseService } from './base';
import { ApiRequest } from '../lib/request';
import type { ApiResponse, ApiListResponse } from '../lib/response';

import { PurchaseOrder, PurchaseOrderItem } from '../models/purchaseOrder';

export class PurchaseOrderService extends BaseService<PurchaseOrder> {
  override model = PurchaseOrder;
  override modelName = 'purchaseOrder';
  override modelLabel = 'Purchase Order';

  async create({
    supplier,
    employee,
    auto = false,
  }: {
    supplier: { idSupplier: number };
    employee?: { idEmployee: number };
    auto?: boolean;
  }): Promise<ApiResponse<PurchaseOrder>> {
    const request = new ApiRequest({
      method: 'POST',
      url: this.modelBaseUrl,
      data: {
        idSupplier: supplier.idSupplier,
        idEmployee: employee?.idEmployee,
        auto: auto ? 1 : 0,
      },
    });

    return await this.requestItem(request, PurchaseOrder);
  }

  /**
   * Filter purchase orders server-side and return the intersection of
   * matching IDs (AND across all active filters).
   *
   * Backend route `POST /purchaseOrder/filter` expects each filter as a
   * `{filter, params}` entry and returns one matched ID list per entry —
   * see `app/Models/Business/Filters/PurchaseOrderFilter.php`. We transform
   * the flat `{key: params}` object the page hands us (the output of
   * `useFilterController.getFilterParams()`) into that envelope, intersect
   * the per-filter ID lists, and hand callers a single `{data: {ids: …}}`
   * shape ready for `listIDs(page)`.
   */
  async filter(
    filters: Record<string, any>,
  ): Promise<ApiResponse<{ ids: number[] }>> {
    const filterArray = Object.entries(filters).map(([filter, params]) => ({
      filter,
      params,
    }));

    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/filter`,
      data: { filters: filterArray },
    });

    const response = await this.request(request);
    const results = (response.data?.filters ?? []) as Array<{ ids: number[] }>;

    let ids: number[] = [];
    if (results.length > 0) {
      const sets = results.map((r) => new Set(r.ids));
      const first = sets[0]!;
      ids = [...first].filter((id) => sets.every((s) => s.has(id)));
    }

    return { ...response, data: { ids } } as ApiResponse<{ ids: number[] }>;
  }

  async listIDs(
    ids: number[],
    page: number = 1,
  ): Promise<ApiListResponse<PurchaseOrder>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/ids`,
      data: { ids },
      query: { page: page.toString() },
    });

    return await this.requestList(request, PurchaseOrder);
  }

  async send(
    purchaseOrder: PurchaseOrder,
  ): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${purchaseOrder.objectID()}/send`,
    });

    return await this.requestItem(request, PurchaseOrder);
  }

  async receive(
    purchaseOrder: PurchaseOrder,
  ): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${purchaseOrder.objectID()}/receive`,
    });

    return await this.requestItem(request, PurchaseOrder);
  }

  async receiveAll(
    purchaseOrder: PurchaseOrder,
  ): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${purchaseOrder.objectID()}/receiveAll`,
    });

    return await this.requestItem(request, PurchaseOrder);
  }

  async receiveItems(
    purchaseOrder: PurchaseOrder,
    items: { idPurchaseOrderItem: number; receivedQuantity: number }[],
  ): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${purchaseOrder.objectID()}/receiveItems`,
      data: {
        items: items.map(({ idPurchaseOrderItem, receivedQuantity }) => ({
          idPurchaseOrderItem,
          receivedQuantity,
        })),
      },
    });

    return await this.requestItem(request, PurchaseOrder);
  }

  async complete(
    purchaseOrder: PurchaseOrder,
  ): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${purchaseOrder.objectID()}/complete`,
    });

    return await this.requestItem(request, PurchaseOrder);
  }

  async addItems(
    purchaseOrder: PurchaseOrder,
    quantities: { product: { idProduct: number }; quantity: number }[],
  ): Promise<ApiResponse<PurchaseOrder>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${purchaseOrder.objectID()}/products`,
      data: {
        quantities: quantities.map(({ product, quantity }) => ({
          product: product.idProduct,
          quantity,
        })),
      },
    });

    return await this.requestItem(request, PurchaseOrder);
  }

  async getItem(
    purchaseOrder: PurchaseOrder,
    purchaseOrderItem: PurchaseOrderItem,
  ): Promise<ApiResponse<PurchaseOrderItem>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${purchaseOrder.objectID()}/products/${purchaseOrderItem.idPurchaseOrderItem}`,
    });

    return await this.requestItem(request, PurchaseOrderItem);
  }

  async saveItem(
    purchaseOrder: PurchaseOrder,
    purchaseOrderItem: PurchaseOrderItem,
  ): Promise<ApiResponse<PurchaseOrderItem>> {
    const request = new ApiRequest({
      method: 'PUT',
      url: `${this.modelBaseUrl}/${purchaseOrder.objectID()}/products/${purchaseOrderItem.idPurchaseOrderItem}`,
      data: purchaseOrderItem.toJSON(),
    });

    return await this.requestItem(request, PurchaseOrderItem);
  }

  async removeItem(
    purchaseOrder: PurchaseOrder,
    purchaseOrderItem: PurchaseOrderItem,
  ): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'DELETE',
      url: `${this.modelBaseUrl}/${purchaseOrder.objectID()}/products/${purchaseOrderItem.idPurchaseOrderItem}`,
    });

    return await this.requestItem(request, PurchaseOrder);
  }
}
